// Daily Flash — 5-Min-Morgenblick (Sprint 6 Push 2: Bento-Restructure).
//
// Top-Block: Bento-Grid mit 8 Tile-Slots (Hero/DecisionQ/TaxMicro/AnomalyMicro/
// BWA-Trend/TaxCalendar/Mahnungen/4× Per-Company) — bento-spec-sprint6.md.
// Severity-Trio (cyan/amber/pink) ORTHOGONAL zu Firmen-Farben (R6).
// InfraX-Per-Company-Card zeigt nur Detail-Page-Hint (R1, kein Holding-Aggregat).
//
// Unterhalb des Bento bleiben die operativen Sektionen aus Sprint 3+4+5:
// Forecast13w-Detail-Trichter, 3-col Operations, TerminBestaetigung, Risk
// (AnomalyStream + Buergschaft), MahnungOutbound, ComplianceBanner.

import { Suspense, lazy, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";

import CockpitTabBar from "../components/CockpitTabBar";
import PipelineStatusStrip from "../components/PipelineStatusStrip";
import { useCockpitTab } from "../hooks/useCockpitTab";
import {
  TAB_TILES,
  TAB_GRID_DESKTOP,
  TAB_GRID_TABLET,
} from "../lib/dashboard-tab-tiles";

import {
  BescheinigungenResponse,
  BuergschaftAggregateResponse,
  BwaResponse,
  CashForecastResponse,
  CashTotalKpi,
  ComplianceResponse,
  EntityKpi,
  Forecast13wKpi,
  ForderungenResponse,
  ForderungsAgingEventsResponse,
  MahnungenEingegangenResponse,
  OutflowForecastResponse,
  PetraCallsResponse,
  PipelineResponse,
  PipelineSignalsResponse,
  RunwayKpi,
  SyncStatus,
  TodayBriefingResponse,
  TodayResponse,
  type LiquiPlanungResponse,
  type LiquiForecastResponse,
  UmsatzLiveResponse,
  UnauthorizedError,
  VacationsResponse,
  AnomalyEventsResponse,
  type FreshnessResponse,
  fetchAnomalyEvents,
  fetchBescheinigungen,
  fetchBuergschaft,
  fetchBwa,
  fetchCashForecast,
  fetchForderungen,
  fetchForderungsAgingEvents,
  fetchKpiCashTotal,
  fetchKpiEntity,
  fetchKpiForecast13w,
  fetchKpiLiquiPlanung,
  fetchKpiRunway,
  fetchLiquiForecast13w,
  fetchMahnungenEingegangen,
  fetchOutflowForecast,
  fetchPetraCalls,
  fetchPipeline,
  fetchPipelineSignals,
  fetchPipelinesFreshness,
  fetchSteuerCompliance90d,
  fetchSyncStatus,
  fetchToday,
  fetchTodayBriefing,
  fetchUmsatzLive,
  fetchVacations,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  aggregateBwaKonzern,
  aggregateBwaRolling3M,
  bwaJahresUmsatz,
  bwaUmsatzSeriesForFirma,
  formatEurCompact,
} from "../lib/bwa-aggregate";

import HamburgerDrawer from "../components/HamburgerDrawer";
import OpModule from "../components/OpModule";
import SyncBanner from "../components/SyncBanner";

import AnomalyStreamTile from "../components/AnomalyStreamTile";
import MahnungOutboundTile from "../components/MahnungOutboundTile";
import TerminBestaetigungTile from "../components/TerminBestaetigungTile";
import BuergschaftTile from "../components/cards/BuergschaftTile";
import ComplianceBanner from "../components/cards/ComplianceBanner";
// Code-Splitting (2026-07-06): die recharts-nutzenden Komponenten sind alle
// below-the-fold (Beweis-Accordion, Cash-/Kanal-Drawer, Büro-Tab) → lazy, damit
// der ~114 kB-gz-recharts-Chunk erst beim Öffnen lädt, nicht beim App-Start.
const ForderungenCard = lazy(() => import("../components/cards/ForderungenCard"));
const PetraCallsCard = lazy(() => import("../components/cards/PetraCallsCard"));
import PipelineSignalsCard from "../components/cards/PipelineSignalsCard";
import TodayBriefingCard from "../components/cards/TodayBriefingCard";
import VacationsCard from "../components/cards/VacationsCard";

import BentoGrid from "../components/bento/BentoGrid";
import BentoTile from "../components/bento/BentoTile";
// HF-2.2-Followup (TS6133-Fix): Bento-Tiles + Severity-Helper, die durch
// HF-2.2 aus dem Desktop-Bento entfernt wurden, sind hier nicht mehr
// importiert. Die Tile-Komponenten selbst bleiben im Repo (für
// __bento_demo + evtl. Re-Aktivierung in Sprint 10).
const BwaTrendBentoTile = lazy(() => import("../components/bento/tiles/BwaTrendBentoTile"));
import PerCompanyBentoTile, {
  type FirmaFrische,
} from "../components/bento/tiles/PerCompanyBentoTile";
import ForecastHeroBentoTile from "../components/bento/tiles/ForecastHeroBentoTile";
import LiquiPlanungBentoTile from "../components/bento/tiles/LiquiPlanungBentoTile";
import TileDrawer from "../components/bento/TileDrawer";
const CashDrawerContent = lazy(() => import("../components/bento/drawers/CashDrawerContent"));
import BwaDrawerContent from "../components/bento/drawers/BwaDrawerContent";
import TaxCalendarDrawerContent from "../components/bento/drawers/TaxCalendarDrawerContent";
import MahnungenDrawerContent from "../components/bento/drawers/MahnungenDrawerContent";
import OutflowDrawerContent from "../components/bento/drawers/OutflowDrawerContent";

import NettoPositionBentoTile from "../components/bento/tiles/NettoPositionBentoTile";
import {
  deriveNetto4W,
  deriveTodayActions,
  deriveUeberfaelligDxGk,
} from "../lib/derive-actions";
import JaKompassBentoTile from "../components/bento/tiles/JaKompassBentoTile";
import BeweisSektion, { type BeweisTrend } from "../components/BeweisSektion";
import LageHeader from "../components/LageHeader";
import ZenFokusDiorama from "../components/zen/ZenFokusDiorama";
import { deriveWetter } from "../lib/derive-wetter";
import KanalInspektion, {
  type KanalStatus,
  type ZulaufDaten,
} from "../components/instrumente/KanalInspektion";
import Fundament2030 from "../components/instrumente/Fundament2030";
import Uhrwerk, { type RadDaten } from "../components/instrumente/Uhrwerk";
import RangierbahnhofAbgangsRadar from "../components/instrumente/RangierbahnhofAbgangsRadar";
import { jahresSchichten, paceRatio } from "../lib/instrumente";
import { fetchFokus, fetchJa, type FokusData, type JaResponse } from "../lib/api";
import TeamTabSections from "../components/team/TeamTabSections";
import TaskRunsPanel from "../components/automation/TaskRunsPanel";

// M-B2: "kanal" = Drawer mit den überfälligen Einzelposten (Wohnort der
// Forderungs-Details; Klick auf die Kanal-Ablagerung).
type DrawerKey = "cash" | "bwa" | "taxCalendar" | "mahnungen" | "outflow" | "kanal";

const POLL_5M = 5 * 60_000;
const POLL_2M = 2 * 60_000;

export default function DashboardPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { tab, setTab } = useCockpitTab();
  const [openDrawer, setOpenDrawer] = useState<DrawerKey | null>(null);
  // AUFTRAG K3: Kanal-Spülung → echter Mahnlauf-Flow (öffnet die
  // Beweis-Sektion Outflow+Mahnstufen und scrollt hin).
  const [mahnlaufSignal, setMahnlaufSignal] = useState(0);
  const enabled = auth.isAuthenticated;
  const baseOpts = {
    enabled,
    refetchInterval: POLL_5M,
    // K7-Reaktivität (Kevin 06.07.): Server-seitige Änderungen (06:00-Pull,
    // Snapshot-Inbox, manueller Saldo aus dem Cash-Eintrag) erscheinen beim
    // Zurückwechseln auf den Tab von selbst — kein Reload nötig.
    refetchOnWindowFocus: true,
    retry: (n: number, err: Error) =>
      !(err instanceof UnauthorizedError) && n < 1,
  };

  // ---- KPIs fuer Bento-Hero + Per-Company-Cards ----
  const syncQuery = useQuery<SyncStatus, Error>({
    queryKey: ["sync", "status"],
    queryFn: fetchSyncStatus,
    ...baseOpts,
  });
  const cashTotalQuery = useQuery<CashTotalKpi, Error>({
    queryKey: ["kpi", "cash-total"],
    queryFn: fetchKpiCashTotal,
    ...baseOpts,
  });
  const runwayQuery = useQuery<RunwayKpi, Error>({
    queryKey: ["kpi", "runway"],
    queryFn: fetchKpiRunway,
    ...baseOpts,
  });
  // DSO/DPO werden Per-Firma via EntityKpi geliefert (Bento Per-Company-Cards).
  // Globale dso/dpo-Endpoints leben in BurnRatePage / Detail-Pages.
  const forecast13wQuery = useQuery<Forecast13wKpi, Error>({
    queryKey: ["kpi", "forecast-13w"],
    queryFn: fetchKpiForecast13w,
    ...baseOpts,
  });
  // Phase F.2 (Sprint 7 Final): Liqui-Planung — Top-3 Termine + Burn-Rate.
  const liquiPlanungQuery = useQuery<LiquiPlanungResponse, Error>({
    queryKey: ["kpi", "liqui-planung"],
    queryFn: fetchKpiLiquiPlanung,
    ...baseOpts,
  });
  // Task 2 (2026-05-20): Aging-basierter Eingangs-Forecast Konzern.
  // Fuettert den Hero-Tile mit echten Eingang-Erwartungen statt 0.
  const liquiForecastQuery = useQuery<LiquiForecastResponse, Error>({
    queryKey: ["liqui", "forecast-13w", "konzern"],
    queryFn: () => fetchLiquiForecast13w("konzern"),
    ...baseOpts,
  });
  const drainxQuery = useQuery<EntityKpi, Error>({
    queryKey: ["kpi", "entity", "drainx"],
    queryFn: () => fetchKpiEntity("drainx"),
    ...baseOpts,
  });
  const gundkQuery = useQuery<EntityKpi, Error>({
    queryKey: ["kpi", "entity", "gundk"],
    queryFn: () => fetchKpiEntity("gundk"),
    ...baseOpts,
  });
  const xholdingQuery = useQuery<EntityKpi, Error>({
    queryKey: ["kpi", "entity", "xholding"],
    queryFn: () => fetchKpiEntity("xholding"),
    ...baseOpts,
  });
  // InfraX-Entity-KPI (Kevin 06.07.): InfraX-SUMMEN mitziehen (Umsatz, offen,
  // überfällig, DSO) — DSGVO/R1-konform NUR Aggregate, KEINE Kunden-Namen
  // (topSchuldner bleibt für InfraX null). Konzern-Consolidation-Whitelist
  // bleibt unverändert (dx+gk+xh), InfraX zeigt nur seine eigenen Zahlen.
  const infraxQuery = useQuery<EntityKpi, Error>({
    queryKey: ["kpi", "entity", "infrax"],
    queryFn: () => fetchKpiEntity("infrax"),
    ...baseOpts,
  });
  // BWA-Aggregat (Konzern-Sum DrainX+G+K+X-Holding) fuer die Hero-Reihe
  // (Sprint 7 Push 2B). Geteilte Quelle fuer Desktop-Bento + Mobile-Hero-Block.
  const bwaQuery = useQuery<BwaResponse, Error>({
    queryKey: ["bwa", "konzern-aggregat"],
    queryFn: () => fetchBwa({ limit: 60 }),
    enabled,
    staleTime: 10 * 60_000,
    refetchInterval: POLL_5M,
    retry: (n: number, err: Error) =>
      !(err instanceof UnauthorizedError) && n < 1,
  });
  // Sprint-7-Push 4: Tooltime-Live-Snapshot (Cowork-Schedule, woechentlich).
  const umsatzLiveQuery = useQuery<UmsatzLiveResponse, Error>({
    queryKey: ["umsatz-live"],
    queryFn: fetchUmsatzLive,
    enabled,
    staleTime: 10 * 60_000,
    refetchInterval: POLL_5M,
    retry: (n: number, err: Error) =>
      !(err instanceof UnauthorizedError) && n < 1,
  });

  // (DecisionQueue-List-for-Count entfernt mit MobileBentoOverview —
  // war deren einziger Konsument; die /decisions-Page hat ihre eigene.)

  // ---- Bento: Tax-Compliance 90T (TaxMicro + TaxCalendar) ----
  const complianceQuery = useQuery<ComplianceResponse, Error>({
    queryKey: ["steuer-compliance-90d"],
    queryFn: fetchSteuerCompliance90d,
    ...baseOpts,
  });
  // nextSteuerItem fuer TaxMicroBentoTile-Severity entfernt (HF-2.2).
  // Compliance-Query bleibt fuer ComplianceBanner (Block #8) + MobileBento.

  // ---- Bento: Anomaly-Stream (Top-Item + Count) ----
  const anomalyQuery = useQuery<AnomalyEventsResponse, Error>({
    queryKey: ["anomaly-events", "open"],
    queryFn: () => fetchAnomalyEvents({ status: "open", limit: 5 }),
    ...baseOpts,
    refetchInterval: POLL_5M,
  });

  // ---- Bento: JA-Kompass (Daten-Inventur 2026-07) ----
  const jaQuery = useQuery<JaResponse, Error>({
    queryKey: ["ja"],
    queryFn: fetchJa,
    enabled,
    staleTime: 60 * 60_000, // JA aendert sich ~1x/Jahr
    retry: (n: number, err: Error) =>
      !(err instanceof UnauthorizedError) && n < 1,
  });

  // AUFTRAG K3: Fundament-Ziel 2030 aus dem Fokus (gleicher Query-Key wie
  // das ZenFokus-Diorama → React Query dedupliziert).
  const fokusQuery = useQuery<FokusData, Error>({
    queryKey: ["fokus"],
    queryFn: fetchFokus,
    enabled,
    staleTime: 10 * 60_000,
    retry: 1,
  });

  // ---- Bento: Mahnungen (Count + Sender) ----
  const mahnungenQuery = useQuery<MahnungenEingegangenResponse, Error>({
    queryKey: ["mahnungen-eingegangen"],
    queryFn: fetchMahnungenEingegangen,
    ...baseOpts,
  });

  // ---- Auftrag H Schritt 3: Datenfrische fuer die Firmen-Kacheln.
  //      Gleicher queryKey wie PipelineStatusStrip → React Query dedupliziert,
  //      es gibt weiterhin nur EINEN /pipelines/freshness-Call. ----
  const freshnessQuery = useQuery<FreshnessResponse, Error>({
    queryKey: ["pipelines-freshness"],
    queryFn: fetchPipelinesFreshness,
    enabled,
    staleTime: 10 * 60_000,
    refetchInterval: 15 * 60_000,
    retry: (n: number, err: Error) =>
      !(err instanceof UnauthorizedError) && n < 1,
  });

  // ---- Operative Sections (unter Bento) ----
  const todayQuery = useQuery<TodayResponse, Error>({
    queryKey: ["cockpit", "today"],
    queryFn: fetchToday,
    ...baseOpts,
  });
  const forecastQuery = useQuery<CashForecastResponse, Error>({
    queryKey: ["cockpit", "cash-forecast"],
    queryFn: fetchCashForecast,
    ...baseOpts,
  });
  const vacationsQuery = useQuery<VacationsResponse, Error>({
    queryKey: ["cockpit", "vacations"],
    queryFn: fetchVacations,
    ...baseOpts,
  });
  const todayBriefingQuery = useQuery<TodayBriefingResponse, Error>({
    queryKey: ["cockpit", "today-briefing"],
    queryFn: fetchTodayBriefing,
    ...baseOpts,
  });
  const forderungenQuery = useQuery<ForderungenResponse, Error>({
    queryKey: ["cockpit", "forderungen"],
    queryFn: fetchForderungen,
    ...baseOpts,
  });
  const petraQuery = useQuery<PetraCallsResponse, Error>({
    queryKey: ["cockpit", "petra-calls"],
    queryFn: fetchPetraCalls,
    enabled,
    refetchInterval: POLL_2M,
    retry: (n, err) => !(err instanceof UnauthorizedError) && n < 1,
  });
  const pipelineQuery = useQuery<PipelineResponse, Error>({
    queryKey: ["cockpit", "pipeline"],
    queryFn: fetchPipeline,
    ...baseOpts,
  });
  const outflowQuery = useQuery<OutflowForecastResponse, Error>({
    queryKey: ["cockpit", "outflow-forecast"],
    queryFn: fetchOutflowForecast,
    ...baseOpts,
  });
  // termine-14d-Query entfernt mit MobileBentoOverview (einziger Konsument;
  // die Termine-Liste starb in M-B4). K5/Rangierbahnhof holt sich die
  // Outflow-Termine später über seinen eigenen /outflow/upcoming-Endpoint.
  const pipelineSignalsQuery = useQuery<PipelineSignalsResponse, Error>({
    queryKey: ["pipeline-signals"],
    queryFn: () => fetchPipelineSignals(10),
    ...baseOpts,
  });
  const agingEventsQuery = useQuery<ForderungsAgingEventsResponse, Error>({
    queryKey: ["forderungs-aging-events"],
    queryFn: () => fetchForderungsAgingEvents(20),
    ...baseOpts,
  });
  const buergschaftQuery = useQuery<BuergschaftAggregateResponse, Error>({
    queryKey: ["buergschaft"],
    queryFn: fetchBuergschaft,
    ...baseOpts,
  });
  const bescheinigungenQuery = useQuery<BescheinigungenResponse, Error>({
    queryKey: ["bescheinigungen"],
    queryFn: () => fetchBescheinigungen(30),
    ...baseOpts,
  });

  // ---- Auth-Gates ----
  if (auth.isLoading) {
    return <FullPageMessage>Session wird geprüft …</FullPageMessage>;
  }
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (
    todayQuery.error instanceof UnauthorizedError ||
    forecastQuery.error instanceof UnauthorizedError
  ) {
    return <Navigate to="/login" replace />;
  }

  // ---- Sprint 7 Push 3: rollierender 3M-Schnitt fuer Umsatz + EBIT ----
  const bwaAgg = aggregateBwaRolling3M(bwaQuery.data?.items);

  // ---- Makeover 2026-07: "Heute handeln" + Netto-4W-Bruecke ----
  const todayActions = deriveTodayActions({
    cash: cashTotalQuery.data,
    mahnungen: mahnungenQuery.data,
    forderungen: forderungenQuery.data,
    umsatzLive: umsatzLiveQuery.data,
    compliance: complianceQuery.data,
    anomaly: anomalyQuery.data,
    pipeline: pipelineQuery.data,
  });
  const netto4w = deriveNetto4W(
    cashTotalQuery.data,
    liquiForecastQuery.data,
    outflowQuery.data,
  );

  // Radikal-Konsolidierung 03.07.: Firmen-Kacheln zeigen EINE grosse echte
  // Zahl — Umsatz YTD netto aus dem taeglichen Tooltime-Snapshot (inkl.
  // InfraX; r1_reference_only erlaubt Einzeldaten, nur Aggregate nicht).
  const umsatzYtdFor = (id: string): string | null =>
    umsatzLiveQuery.data?.firmas.find((f) => f.firma_id === id)
      ?.umsatz_ytd_netto_eur ?? null;

  // Auftrag H Schritt 3: 12M-BWA-Umsatz je Firma (Sparkline im Firmen-Hex)
  // + schlechtester Freshness-Status der Kachel-Datenstrecken (tooltime/bwa).
  const umsatz12mFor = (id: string): number[] =>
    bwaUmsatzSeriesForFirma(bwaQuery.data?.items, id);

  const frischeFor = (
    id: string,
  ): { status: FirmaFrische | null; hint: string | null } => {
    const items = (freshnessQuery.data?.items ?? []).filter(
      (i) =>
        (i.datenart === "tooltime_snapshot" || i.datenart === "bwa") &&
        (i.firma === id || i.firma == null),
    );
    if (items.length === 0) return { status: null, hint: null };
    const worst = items.some((i) => i.status === "missing")
      ? "missing"
      : items.some((i) => i.status === "stale")
        ? "stale"
        : items.some((i) => i.status === "ok")
          ? "ok"
          : null; // nur "unbekannt" → kein Dot statt falscher Sicherheit
    const hint = items
      .map((i) => `${i.label}: ${i.status}${i.alter_tage != null ? ` (${i.alter_tage} T)` : ""}`)
      .join("\n");
    return { status: worst, hint };
  };
  const frischeDrainx = frischeFor("drainx");
  const frischeGundk = frischeFor("gundk");
  const frischeInfrax = frischeFor("infrax");
  const frischeXholding = frischeFor("xholding");

  // Auftrag H Schritt 4: Kopf-Kennzahlen der Beweis-Sektionen — jede
  // Sektion beantwortet sich in EINER Zahl, bevor sie aufgeklappt wird.
  const eurNum = (v: string | null | undefined): number | null => {
    if (v == null || v === "") return null;
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  // M-B3: die EBIT-Solo-Karte ist tot — EBIT wohnt im BWA-Accordion-Header
  // ("BWA-Trend — EBIT +20k€/M ↗"): Kennzahl = 3M-Ø-EBIT, Pfeil = Delta
  // zum vorherigen 3M-Fenster.
  const ebitDelta = bwaAgg?.comparison?.ebitDeltaAbs ?? null;
  const bwaKennzahl = bwaAgg
    ? `EBIT ${bwaAgg.ebitAvg >= 0 ? "+" : ""}${formatEurCompact(bwaAgg.ebitAvg)}/M`
    : null;
  const bwaTrend: BeweisTrend | null =
    ebitDelta == null
      ? null
      : ebitDelta > 1000
        ? { richtung: "up", ton: "ok" }
        : ebitDelta < -1000
          ? { richtung: "down", ton: "warn" }
          : { richtung: "flat" };
  const f13 = forecast13wQuery.data;
  const forecastEnd =
    f13 && f13.status !== "no_data"
      ? eurNum(f13.mode === "kontostand" ? f13.end_saldo_eur : f13.net_movement_eur)
      : null;
  const forecastKennzahl =
    forecastEnd != null
      ? `${formatEurCompact(forecastEnd)} · ${f13!.mode === "kontostand" ? "Endstand 13W" : "Netto 13W"}`
      : null;
  // M-B5: Badge des Büro-Tabs = operative Signale (Mails/Petra/Termine/
  // Urlaub/Pipeline). Forderungen zählen nicht mehr mit — die wohnen im
  // Kanal, nicht im Büro.
  const bueroSignale =
    (todayBriefingQuery.data?.heute_kritisch?.length ?? 0) +
    (petraQuery.data?.n_today ?? 0) +
    (vacationsQuery.data?.coming_2_weeks?.length ?? 0) +
    (pipelineSignalsQuery.data?.items.length ?? 0);
  const riskCrit = anomalyQuery.data?.n_critical_open ?? 0;

  // AUFTRAG M B1: Ring/Pegel/Solo-Strecke sind gestorben — VJ + Tag-im-Jahr
  // speisen jetzt die Pace-Mikro-Zeilen der Firmen-Kacheln (+ Uhrwerk).
  const jetzt = new Date();
  const tagImJahrPct =
    (jetzt.getTime() - new Date(jetzt.getFullYear(), 0, 1).getTime()) /
    (365 * 86_400_000);
  const ueberfaelligDxGk = deriveUeberfaelligDxGk(umsatzLiveQuery.data).sum;
  const vjJahr = (umsatzLiveQuery.data?.year ?? jetzt.getFullYear()) - 1;
  const vjDrainx = bwaJahresUmsatz(bwaQuery.data?.items, "drainx", vjJahr);
  const vjGundk = bwaJahresUmsatz(bwaQuery.data?.items, "gundk", vjJahr);

  // AUFTRAG K2: Lage-Score — DAS zentrale Stimmungs-Signal (Masterplan §2).
  // Der ZenFokus-Baum liest ihn; nichts definiert eigene Schwellen.
  // Gruppen-Pace fürs Wetter (R1: Konzern = drainx+gundk, ohne InfraX) —
  // koppelt die Umsatz-Gesamtlage an die Baum-Stimmung.
  const gruppenPaceWetter = (() => {
    const pd = paceRatio(eurNum(umsatzYtdFor("drainx")), vjDrainx, tagImJahrPct);
    const pg = paceRatio(eurNum(umsatzYtdFor("gundk")), vjGundk, tagImJahrPct);
    const vals = [pd, pg].filter((v): v is number => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  })();
  const wetter = deriveWetter({
    netto: netto4w,
    ueberfaelligEur: ueberfaelligDxGk,
    actions: todayActions,
    mahnungen: mahnungenQuery.data,
    freshness: freshnessQuery.data,
    // Gesamtlage: Runway + Umsatz-Pace fließen in die Baum-Stimmung ein.
    reichweiteTage: liquiPlanungQuery.data?.reichweite_tage ?? null,
    gruppenPace: gruppenPaceWetter,
  });
  // Daten-Kontrakt (Masterplan §3): missing solange die Kern-Queries laden,
  // error friert das Wetter auf dem letzten Score ein.
  const wetterBereit = !(cashTotalQuery.isLoading && umsatzLiveQuery.isLoading);
  const wetterFehler = Boolean(cashTotalQuery.error || umsatzLiveQuery.error);

  // ---- AUFTRAG K3: Instrumenten-Datenbindung (I1–I3) ----
  const starteMahnlaufFlow = () => {
    setMahnlaufSignal((s) => s + 1);
    document
      .getElementById("beweis-outflow")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  // M-B2: Zone-A-Action "Mahnlauf fällig" verlinkt zur Kanal-Spülung
  // (der EINE CTA) — kein eigener Drawer, sondern Sprung zum Instrument.
  const openDrawerOderKanal = (d: DrawerKey) => {
    if (d === "kanal") {
      document
        .getElementById("kanal-panel")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setOpenDrawer(d);
  };
  const topSchuldnerFor = (
    kpi: EntityKpi | undefined,
  ): { kunde: string; betragEur: number } | null => {
    const t = kpi?.top_overdue?.[0];
    if (!t) return null;
    const betrag = Number.parseFloat(t.betrag_brutto_eur);
    return Number.isFinite(betrag) ? { kunde: t.kunde, betragEur: betrag } : null;
  };
  const kanalZulaeufe: ZulaufDaten[] = [
    {
      firma: "drainx",
      umsatzYtdEur: eurNum(umsatzYtdFor("drainx")),
      dsoDays: drainxQuery.data?.dso_days ?? null,
      offenEur: eurNum(drainxQuery.data?.forderungen_eur),
      ueberfaelligEur: eurNum(drainxQuery.data?.forderungen_ueberfaellig_eur),
      topSchuldner: topSchuldnerFor(drainxQuery.data),
      frische: frischeDrainx.status,
    },
    {
      firma: "gundk",
      umsatzYtdEur: eurNum(umsatzYtdFor("gundk")),
      dsoDays: gundkQuery.data?.dso_days ?? null,
      offenEur: eurNum(gundkQuery.data?.forderungen_eur),
      ueberfaelligEur: eurNum(gundkQuery.data?.forderungen_ueberfaellig_eur),
      topSchuldner: topSchuldnerFor(gundkQuery.data),
      frische: frischeGundk.status,
    },
    {
      // InfraX: SUMMEN mitziehen (offen, überfällig, DSO, Umsatz) — aber
      // R1/DSGVO: KEIN topSchuldner (kein Kunden-Name aus dem Bahn-Datenraum).
      firma: "infrax",
      umsatzYtdEur: eurNum(umsatzYtdFor("infrax")),
      dsoDays: infraxQuery.data?.dso_days ?? null,
      offenEur: eurNum(infraxQuery.data?.forderungen_eur),
      ueberfaelligEur: eurNum(infraxQuery.data?.forderungen_ueberfaellig_eur),
      topSchuldner: null,
      frische: frischeInfrax.status,
    },
  ];
  const kanalStatus: KanalStatus =
    drainxQuery.error || gundkQuery.error
      ? "error"
      : !umsatzLiveQuery.data?.available
        ? "missing"
        : (umsatzLiveQuery.data.age_hours ?? 0) > 8 * 24
          ? "stale"
          : "ok";
  const kanalStand = umsatzLiveQuery.data?.snapshot_iso
    ? new Date(umsatzLiveQuery.data.snapshot_iso).toLocaleDateString("de-DE")
    : null;

  const vjInfrax = bwaJahresUmsatz(bwaQuery.data?.items, "infrax", vjJahr);
  const uhrwerkRaeder: RadDaten[] = [
    { firma: "drainx", paceRatio: paceRatio(eurNum(umsatzYtdFor("drainx")), vjDrainx, tagImJahrPct) },
    { firma: "gundk", paceRatio: paceRatio(eurNum(umsatzYtdFor("gundk")), vjGundk, tagImJahrPct) },
    { firma: "infrax", paceRatio: paceRatio(eurNum(umsatzYtdFor("infrax")), vjInfrax, tagImJahrPct) },
  ];

  const konzernJa = aggregateBwaKonzern(bwaQuery.data?.items);
  const fundamentSchichten = jahresSchichten(
    jaQuery.data?.eintraege ?? [],
    jetzt.getFullYear(),
    konzernJa?.ebitYtd ?? null,
  );
  const fundamentZielEur =
    fokusQuery.data?.ziele?.find((z) => z.jahr === 2030)?.betrag_eur ?? null;
  const bwaStale = (freshnessQuery.data?.items ?? []).some(
    (i) => i.datenart === "bwa" && (i.status === "stale" || i.status === "missing"),
  );

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-md sticky top-0 z-10 pt-[env(safe-area-inset-top)]">
        <HamburgerDrawer email={auth.me?.email} />
        <div className="hidden md:flex px-4 sm:px-6 py-3 items-center justify-between gap-3">
          <h1 className="text-sm sm:text-base font-semibold text-slate-100 tracking-tight flex items-center gap-2">
            {/* Restraint (§Motion): keine Dauer-Pulse — der bento-pulse auf
                critical bleibt die einzige Dauerbewegung im Cockpit. */}
            <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
            <span>X-Holding</span>
            <span className="text-slate-600 font-normal">·</span>
            <span className="text-slate-300 font-normal">Daily Flash</span>
          </h1>
          <span className="text-xs text-slate-400 truncate">{auth.me?.email}</span>
        </div>
        <CockpitTabBar
          active={tab}
          onChange={setTab}
          badges={{ buero: bueroSignale }}
        />
      </header>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:rounded-lg focus:bg-sky-500 focus:text-white focus:px-3 focus:py-2 focus:text-xs focus:font-medium"
      >
        Direkt zum Hauptinhalt springen
      </a>

      <main id="main-content" className="px-4 sm:px-6 py-4 pb-20 md:pb-4 space-y-5">
        {/* ============== EBENE 1: DIE LAGE (Auftrag K2, Masterplan §1) ======
            Zone A = ZenFokus-Diorama + Lage-Satz/Hero/Actions + Fundament.
            Mobile-Integration (PC2, "Ebene 1 = ein Screen"): mobile-first
            gestapelt (Baum → Lage → Fundament), ab lg 2-spaltig, ab xl
            3-spaltig. Die alte MobileBentoOverview ist retired — EINE
            responsive Seite (Kevin 06.07.: "am Handy fehlt Baum & co"). */}
        <div className="grid gap-3 lg:grid-cols-[minmax(280px,2fr)_5fr] xl:grid-cols-[minmax(280px,2fr)_5fr_minmax(190px,1.5fr)] items-stretch">
          <ZenFokusDiorama
            wetter={wetterBereit ? wetter : null}
            fehler={wetterFehler}
            kritischeAnomalien={riskCrit > 0}
          />
          <LageHeader
            actions={todayActions}
            netto={netto4w}
            cash={cashTotalQuery.data}
            umsatzLive={umsatzLiveQuery.data}
            mahnungen={mahnungenQuery.data}
            pipeline={pipelineQuery.data}
            liqui={liquiPlanungQuery.data}
            loading={
              cashTotalQuery.isLoading ||
              mahnungenQuery.isLoading ||
              complianceQuery.isLoading
            }
            onOpenDrawer={(d) => openDrawerOderKanal(d)}
          />
          {/* Fundament 2030 klein neben dem Baum (zwei Zeitskalen derselben
              Reise). Auf Mobile mitgestapelt, auf lg ausgeblendet (2-Spalten
              haben keinen Platz), ab xl 3. Spalte. */}
          <div className="block lg:hidden xl:block">
            <Fundament2030
              schichten={fundamentSchichten}
              zielEur={fundamentZielEur}
              stale={bwaStale}
              onDetail={() => setOpenDrawer("bwa")}
            />
          </div>
        </div>
        {/* Ticker direkt unter der Lage (Spec §4.2) — sichtbar auf jedem Tab
            und auch mobil; tote Datenstrecken bleiben so laut wie die Zahlen. */}
        <PipelineStatusStrip />
        <SyncBanner data={syncQuery.data} loading={syncQuery.isLoading} />

        {/* MOBILE OVERVIEW retired (Kevin 06.07.): die separate
            MobileBentoOverview ist weg — die echten Zonen (A/C/Bento/D) sind
            jetzt mobile-first responsive und ersetzen sie. Eine Seite,
            ein Datenpfad. */}

        {/* ============== LEITSTAND-METAPHERN (Auftrag H Schritt 6) ==============
            V4.2-Scaffolds mit korrekter Datenbindung + Aufbau-Choreografie
            (Ring-Sweep → Rohre füllen → Zug fährt, gestaffelt, <2,5s).
            Die cinematische Politur (Wellen-Parallax, Leck-Drip, ICE-Trail,
            Glow, 60fps-Check) macht die Cowork-Screenshot-Session. */}
        {/* AUFTRAG M B1/B2 + K6 (docs/AUFTRAG-M, K-INTEGRATION): Instrumente
            sind Verdichter. Zone C = Kanal (Forderungen, breit) +
            Rangierbahnhof (Abflüsse, breit — absorbiert die Outflow-4W-Karte,
            M-B4) + Uhrwerk (Tempo). Donut/Pace-Bars/Solo-Strecke/Manometer
            sind gestorben. */}
        {tab === "geld" && (
          <section aria-label="Instrumente" className="block space-y-3">
            <div id="kanal-panel">
              <LeitstandPanel>
                <KanalInspektion
                  zulaeufe={kanalZulaeufe}
                  status={kanalStatus}
                  standLabel={kanalStand}
                  onMahnlauf={starteMahnlaufFlow}
                  onPosten={() => setOpenDrawer("kanal")}
                />
              </LeitstandPanel>
            </div>
            {/* K6: Rangierbahnhof = DIE Abfluss-Ansicht. Absorbiert die
                Outflow-4W-Karte (M-B4) — die ist damit gestorben. Wagen =
                Posten aus /api/v1/outflow/upcoming (K5). */}
            <LeitstandPanel>
              <RangierbahnhofAbgangsRadar />
            </LeitstandPanel>
            <LeitstandPanel>
              <div className="mx-auto max-w-sm">
                <Uhrwerk
                  raeder={uhrwerkRaeder}
                  stale={kanalStatus === "stale"}
                  onFirma={(slug) => navigate(`/firmen/${slug}`)}
                />
              </div>
            </LeitstandPanel>
          </section>
        )}

        {/* ============== BENTO BLOCK (Sprint 10: per-Tab-Tiles) ============== */}
        {TAB_TILES[tab].length > 0 && (
          <section aria-label="Bento Cockpit Overview" className="block">
            <BentoGrid
              desktopStyle={TAB_GRID_DESKTOP[tab]}
              tabletStyle={TAB_GRID_TABLET[tab]}
            >
              {TAB_TILES[tab].includes("netto") && (
                <BentoTile
                  area="netto"
                  size="2x1"
                  severity={netto4w.severity}
                  ariaLabel="Netto-Position 4 Wochen"
                >
                  <NettoPositionBentoTile
                    netto={netto4w}
                    loading={outflowQuery.isLoading || liquiForecastQuery.isLoading}
                  />
                </BentoTile>
              )}
              {TAB_TILES[tab].includes("jaKompass") && (
                <BentoTile area="jaKompass" size="2x2" ariaLabel="Jahresabschluss-Kompass">
                  <JaKompassBentoTile data={jaQuery.data} loading={jaQuery.isLoading} />
                </BentoTile>
              )}
              {TAB_TILES[tab].includes("firmaDrainx") && (
                <BentoTile area="firmaDrainx" size="3x1" firma="drainx" ariaLabel="DrainX" onClick={() => navigate("/firmen/drainx")}>
                  <PerCompanyBentoTile firma="drainx" data={drainxQuery.data} umsatzYtdNettoEur={umsatzYtdFor("drainx")} umsatz12m={umsatz12mFor("drainx")} frische={frischeDrainx.status} frischeHint={frischeDrainx.hint} vjGesamtEur={vjDrainx} tagImJahrPct={tagImJahrPct} flowIndex={0} loading={drainxQuery.isLoading} />
                </BentoTile>
              )}
              {TAB_TILES[tab].includes("firmaGundk") && (
                <BentoTile area="firmaGundk" size="3x1" firma="gundk" ariaLabel="G+K" onClick={() => navigate("/firmen/gundk")}>
                  <PerCompanyBentoTile firma="gundk" data={gundkQuery.data} umsatzYtdNettoEur={umsatzYtdFor("gundk")} umsatz12m={umsatz12mFor("gundk")} frische={frischeGundk.status} frischeHint={frischeGundk.hint} vjGesamtEur={vjGundk} tagImJahrPct={tagImJahrPct} flowIndex={1} loading={gundkQuery.isLoading} />
                </BentoTile>
              )}
              {TAB_TILES[tab].includes("firmaInfrax") && (
                <BentoTile area="firmaInfrax" size="3x1" firma="infrax" ariaLabel="InfraX" onClick={() => navigate("/firmen/infrax")}>
                  <PerCompanyBentoTile firma="infrax" umsatzYtdNettoEur={umsatzYtdFor("infrax")} umsatz12m={umsatz12mFor("infrax")} frische={frischeInfrax.status} frischeHint={frischeInfrax.hint} vjGesamtEur={vjInfrax} tagImJahrPct={tagImJahrPct} flowIndex={2} />
                </BentoTile>
              )}
              {TAB_TILES[tab].includes("firmaXholding") && (
                <BentoTile area="firmaXholding" size="3x1" firma="xholding" ariaLabel="X-Holding" onClick={() => navigate("/firmen/xholding")}>
                  <PerCompanyBentoTile firma="xholding" data={xholdingQuery.data} umsatzYtdNettoEur={umsatzYtdFor("xholding")} umsatz12m={umsatz12mFor("xholding")} frische={frischeXholding.status} frischeHint={frischeXholding.hint} flowIndex={3} loading={xholdingQuery.isLoading} />
                </BentoTile>
              )}
            </BentoGrid>
          </section>
        )}

        {/* ============== TAB PANELS (Sprint 10 Phase 1) ==============
            BentoGrid bleibt auf ALLEN Tabs sichtbar (Hero-Overview).
            Sections darunter sind per Tab gruppiert. Mobile zeigt alles
            (MobileBentoOverview hat eigenes Layout). */}

        {/* --- Tab: Geld ---
            Live-Bugfix 03.07.: Forecast13wTile HIER entfernt — der 13W-
            Forecast war auf dem Geld-Tab ZWEIMAL zu sehen (Bento-Karte
            'forecastHero' + dieselbe Kurve nochmal als Full-Width-Tile
            direkt darunter, inkl. identischer GewSt-Zeile). Eine Info,
            ein Ort: die Bento-Karte bleibt, das Duplikat fliegt. */}

        {/* --- Ebene 3: Beweise (Auftrag H Schritt 4, Spec §3+§4.4) ---
            Progressive Disclosure: Charts + operative Module leben hinter
            eingeklappten Sektionen. Kopf = Titel + EINE Kennzahl +
            Trendpfeil; Erstbesuch des Tages: alles zu (localStorage). */}
        {tab === "geld" && (
          <div className="block space-y-3">
            <BeweisSektion
              id="bwa"
              titel="BWA-Trend"
              kennzahl={bwaKennzahl}
              trend={bwaTrend}
              onDetail={() => setOpenDrawer("bwa")}
            >
              <BeweisPanel className="h-[320px]">
                <Suspense fallback={<div className="h-full animate-pulse rounded bg-leitstand-panel" />}>
                  <BwaTrendBentoTile />
                </Suspense>
              </BeweisPanel>
            </BeweisSektion>

            <BeweisSektion
              id="forecast-liqui"
              titel="Forecast + Liquidität"
              kennzahl={forecastKennzahl}
            >
              <div className="grid gap-3 lg:grid-cols-2 items-stretch">
                <BeweisPanel className="min-h-[280px]">
                  <ForecastHeroBentoTile
                    data={forecast13wQuery.data}
                    liquiData={liquiForecastQuery.data}
                    loading={forecast13wQuery.isLoading}
                  />
                </BeweisPanel>
                <BeweisPanel className="min-h-[280px]">
                  <LiquiPlanungBentoTile
                    data={liquiPlanungQuery.data}
                    loading={liquiPlanungQuery.isLoading}
                  />
                </BeweisPanel>
              </div>
            </BeweisSektion>

            {/* M-B2: Outflow-4W wohnt in Zone C; "Mahnungen gegen uns" wohnt
                als Zone-A-KPI + Drawer. Hier bleibt nur der Outbound-Status. */}
            <BeweisSektion
              id="outflow-mahnstufen"
              titel="Mahn-Outbound-Status"
              erzwingeOffen={mahnlaufSignal}
              anchorId="beweis-outflow"
            >
              <MahnungOutboundTile />
            </BeweisSektion>

            {/* M-B5: die Operativ-Module (Mails, Petra, Urlaub, Pipeline)
                sind aus dem Geld-Tab ins Büro gezogen. Hier bleibt nur
                Risk (finanzielle Anomalien + Bürgschaften). */}
            <BeweisSektion
              id="risk"
              titel="Risk"
              kennzahl={`${riskCrit} kritisch`}
              trend={riskCrit > 0 ? { richtung: "up", ton: "critical" } : null}
            >
              <div className="grid gap-3 lg:grid-cols-2 items-start">
                <AnomalyStreamTile />
                <BuergschaftTile
                  data={buergschaftQuery.data}
                  loading={buergschaftQuery.isLoading}
                />
              </div>
            </BeweisSektion>
          </div>
        )}

        {/* --- Tab: Büro (M-B5) — Operatives raus aus dem Geld-Tab.
            Mails, Petra-Anrufe, Termine, Urlaub, Pipeline-Signale. Der
            Pipeline-Donut ist gestorben (Angebots-Zahl steckt in der
            G+K-Kachel + Zone-A-KPI); Forderungen wohnen im Kanal-Drawer. */}
        {tab === "buero" && (
          <div className="block space-y-3">
            <section
              aria-label="Büro"
              className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 items-start"
            >
              <OpModule
                variant="heute"
                title="Heute relevant"
                subtitle="Mail-Briefing + Petra-Anrufe"
                badgeCount={
                  (todayBriefingQuery.data?.heute_kritisch?.length ?? 0) +
                  (petraQuery.data?.n_today ?? 0)
                }
              >
                {todayBriefingQuery.data && (
                  <TodayBriefingCard data={todayBriefingQuery.data} />
                )}
                {petraQuery.data && (
                  <Suspense fallback={<SkeletonRow />}>
                    <PetraCallsCard data={petraQuery.data} />
                  </Suspense>
                )}
                {todayBriefingQuery.isLoading && <SkeletonRow />}
              </OpModule>

              <OpModule
                variant="termine"
                title="Urlaub & Geburtstage"
                subtitle="Nächste 2 Wochen"
                badgeCount={vacationsQuery.data?.coming_2_weeks?.length ?? 0}
              >
                {vacationsQuery.data && <VacationsCard data={vacationsQuery.data} />}
                {vacationsQuery.isLoading && <SkeletonRow />}
              </OpModule>

              <OpModule
                variant="pipeline"
                title="Pipeline-Signale"
                subtitle="Briefing-Signale (Angebote/Nachfass)"
                badgeCount={pipelineSignalsQuery.data?.items.length ?? 0}
              >
                <PipelineSignalsCard
                  data={pipelineSignalsQuery.data}
                  loading={pipelineSignalsQuery.isLoading}
                />
                {!pipelineSignalsQuery.isLoading &&
                  (pipelineSignalsQuery.data?.items.length ?? 0) === 0 && (
                    <p className="text-xs text-slate-500">
                      Keine offenen Signale. Angebots-Pipeline steht in der
                      G+K-Kachel + Zone-A-KPI, Forderungen im Kanal.
                    </p>
                  )}
              </OpModule>
            </section>
            {/* Automatisierung — letzte Läufe der Cowork-Scheduled-Tasks
                (Status + Kurz-Summary + Zeit). Additiv, self-fetching. */}
            <TaskRunsPanel />
          </div>
        )}

        {/* --- Tab: Team --- */}
        {tab === "team" && (
          <div className="block space-y-5">
            <TeamTabSections />
            <TerminBestaetigungTile />
            <ComplianceBanner data={bescheinigungenQuery.data} />
          </div>
        )}

        {/* Fehler-Footer */}
        {todayQuery.error && !(todayQuery.error instanceof UnauthorizedError) && (
          <p className="text-sm text-rose-300">
            Today-Fehler: {todayQuery.error.message}
          </p>
        )}
        {forecastQuery.error && !(forecastQuery.error instanceof UnauthorizedError) && (
          <p className="text-sm text-rose-300">
            Forecast-Fehler: {forecastQuery.error.message}
          </p>
        )}
      </main>

      {/* ============== TILE DRAWERS (Sprint 6 Push 2 CP3) ============== */}
      {/* M-B2: Die überfälligen Einzelposten wohnen im Kanal-Drawer —
          Klick auf die Ablagerung öffnet ihn, keine eigene Karte mehr. */}
      <TileDrawer
        isOpen={openDrawer === "kanal"}
        onClose={() => setOpenDrawer(null)}
        title="Überfällige Forderungen"
        subtitle="Ablagerung im Kanal · DrainX + G+K (InfraX: eigener Datenraum)"
      >
        <div className="space-y-4">
          {forderungenQuery.data && (
            <Suspense fallback={<div className="h-40 animate-pulse rounded bg-leitstand-panel" />}>
              <ForderungenCard
                data={forderungenQuery.data}
                agingEvents={agingEventsQuery.data?.items}
              />
            </Suspense>
          )}
          {(gundkQuery.data?.top_overdue?.length ?? 0) > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                G+K · größte überfällige Posten
              </p>
              <ul className="space-y-1">
                {gundkQuery.data!.top_overdue!.map((t, i) => (
                  <li
                    key={`${t.kunde}-${i}`}
                    className="flex items-baseline justify-between gap-2 text-xs"
                  >
                    <span className="min-w-0 truncate text-slate-200">
                      {t.kunde}
                      <span className="text-slate-500">
                        {" "}
                        · {t.tage_ueberfaellig} T · Stufe {t.mahnstufe}
                      </span>
                    </span>
                    <span className="tabular-nums text-slate-100 shrink-0">
                      {formatEurCompact(Number.parseFloat(t.betrag_brutto_eur) || 0)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </TileDrawer>
      <TileDrawer
        isOpen={openDrawer === "cash"}
        onClose={() => setOpenDrawer(null)}
        title="Cash konsolidiert"
        subtitle="X-Holding Konzern · xholding+drainx+gundk (R1, ohne InfraX)"
      >
        <Suspense fallback={<div className="h-40 animate-pulse rounded bg-leitstand-panel" />}>
          <CashDrawerContent
            cash={cashTotalQuery.data}
            runway={runwayQuery.data}
            drainx={drainxQuery.data}
            gundk={gundkQuery.data}
            xholding={xholdingQuery.data}
          />
        </Suspense>
      </TileDrawer>

      <TileDrawer
        isOpen={openDrawer === "bwa"}
        onClose={() => setOpenDrawer(null)}
        title="BWA-Trend Detail"
        subtitle="DrainX + G+K · 12 Monate · JA-Anker"
      >
        <BwaDrawerContent />
      </TileDrawer>

      <TileDrawer
        isOpen={openDrawer === "taxCalendar"}
        onClose={() => setOpenDrawer(null)}
        title="Tax-Compliance 90 Tage"
        subtitle="Steuer + USt-VA + Bescheinigungs-Ablauf"
      >
        <TaxCalendarDrawerContent
          data={complianceQuery.data}
          loading={complianceQuery.isLoading}
        />
      </TileDrawer>

      <TileDrawer
        isOpen={openDrawer === "mahnungen"}
        onClose={() => setOpenDrawer(null)}
        title="Mahnungen eingegangen"
        subtitle="Glaeubiger-Liste mit Status + Beträgen"
      >
        <MahnungenDrawerContent
          data={mahnungenQuery.data}
          loading={mahnungenQuery.isLoading}
        />
      </TileDrawer>

      <TileDrawer
        isOpen={openDrawer === "outflow"}
        onClose={() => setOpenDrawer(null)}
        title="Cash-Outflow 4 Wochen"
        subtitle="Was geht ab — pro Firma, chronologisch"
      >
        <OutflowDrawerContent
          data={outflowQuery.data}
          loading={outflowQuery.isLoading}
        />
      </TileDrawer>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="space-y-2">
      <div className="h-4 w-2/3 rounded bg-slate-800/60 animate-pulse" />
      <div className="h-4 w-1/2 rounded bg-slate-800/60 animate-pulse" />
    </div>
  );
}

/** Panel der Leitstand-Metaphern — Leitstand-Tokens (V4.2: Panel-Fläche
 *  + Hairline statt slate-Border). */
function LeitstandPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-leitstand-hairline bg-leitstand-panel p-4 sm:p-5">
      {children}
    </div>
  );
}

/** Panel-Wrapper fuer Ex-Bento-Tiles innerhalb der Beweis-Sektionen —
 *  ersetzt den BentoTile-Rahmen (dort kam die Hoehe aus dem Grid). */
function BeweisPanel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-slate-700/50 bg-slate-900/40 p-4 sm:p-6 ${className ?? ""}`}
    >
      {children}
    </div>
  );
}

function FullPageMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">
      {children}
    </div>
  );
}
